import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import logoImage from "../../../assets/images/logo.png";

export const Footer = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const watermarkSvgRef = useRef<SVGSVGElement>(null);
  const watermarkTextRef = useRef<SVGTextElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(660);
  const [windowHeight, setWindowHeight] = useState<number>(typeof window !== "undefined" ? window.innerHeight : 800);
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Measure content height dynamically to ensure perfect responsiveness
  useEffect(() => {
    if (!contentRef.current) return;
    let rAFId: number;

    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
      if (!contentRef.current) return;
      const scrollH = contentRef.current.scrollHeight;
      const offsetH = contentRef.current.offsetHeight;
      const rectH = contentRef.current.getBoundingClientRect().height;
      const measured = Math.ceil(Math.max(scrollH, offsetH, rectH));
      if (measured > 0) {
        setContentHeight((prev) => (Math.abs(prev - measured) > 1 ? measured : prev));
      }
    };

    const debouncedUpdate = () => {
      cancelAnimationFrame(rAFId);
      rAFId = requestAnimationFrame(updateHeight);
    };

    updateHeight();
    const observer = new ResizeObserver(debouncedUpdate);
    observer.observe(contentRef.current);
    window.addEventListener("resize", debouncedUpdate);

    if ((document as any).fonts && (document as any).fonts.ready) {
      (document as any).fonts.ready.then(debouncedUpdate);
    }

    return () => {
      cancelAnimationFrame(rAFId);
      observer.disconnect();
      window.removeEventListener("resize", debouncedUpdate);
    };
  }, []);

  // Fit watermark text dynamically into SVG viewBox — 70% visible, fading down
  useEffect(() => {
    const fitWatermark = () => {
      if (!watermarkSvgRef.current || !watermarkTextRef.current) return;
      try {
        const bbox = watermarkTextRef.current.getBBox();
        if (bbox && bbox.width > 0 && bbox.height > 0) {
          // Exactly 70% visible, 30% cut off
          const visibleHeight = bbox.height * 0.7;
          watermarkSvgRef.current.setAttribute(
            "viewBox",
            `${bbox.x} ${bbox.y} ${bbox.width} ${visibleHeight}`
          );
        }
      } catch (e) {}
    };

    if ((document as any).fonts && (document as any).fonts.ready) {
      (document as any).fonts.ready.then(fitWatermark);
    } else {
      window.addEventListener("load", fitWatermark);
    }
    window.addEventListener("resize", fitWatermark);
    fitWatermark();

    return () => {
      window.removeEventListener("resize", fitWatermark);
      window.removeEventListener("load", fitWatermark);
    };
  }, []);

  // Motion UI scroll-linked entrance physics
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end end"],
  });

  const isTall = contentHeight > windowHeight;
  const parallaxStart = isTall ? (contentHeight - windowHeight) : -40;

  const scale = useTransform(scrollYProgress, [0, 1], [isTall ? 1 : 0.94, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.75, 1], [isTall ? 1 : 0.4, isTall ? 1 : 0.88, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [parallaxStart, 0]);

  const navigationLinks = [
    { name: "Docs", href: "/docs" },
    { name: "Security & SOC 2", href: "/security" },
  ];

  const companyLinks = [
    { name: "About Cynaps", href: "/about" },
    { name: "Blog", href: "/blog" },
    { name: "Privacy Policy", href: "/privacy" },
  ];

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail("");
      setTimeout(() => setIsSubscribed(false), 4000);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden"
      style={{
        clipPath: "polygon(0% 0, 100% 0%, 100% 100%, 0 100%)",
        height: contentHeight ? `${contentHeight}px` : "auto",
      }}
    >
      <div
        className="fixed bottom-0 left-0 w-full z-0 pointer-events-auto"
        style={{
          height: contentHeight ? `${contentHeight}px` : "auto",
        }}
      >
        <motion.div
          ref={contentRef}
          style={{
            scale,
            opacity,
            y,
            transformOrigin: "bottom center",
          }}
          className="w-full bg-black text-white pt-8 sm:pt-10 pb-0 px-4 sm:px-6 md:px-8 font-sans relative overflow-hidden"
        >
          {/* Main Footer Container */}
          <div className="max-w-[1150px] mx-auto grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4 items-stretch relative z-10">
            {/* Left Card — Video Background (Animation Component kept intact with vibrant blue) */}
            <div
              className="relative min-h-[280px] lg:min-h-[300px] rounded-[28px] p-8 overflow-hidden flex flex-col justify-between"
              style={{
                backgroundColor: "#1e4fc0",
                boxShadow: "0 12px 40px rgba(21, 76, 189, 0.25)",
              }}
            >
              {/* Background Video */}
              <video
                className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              >
                <source
                  src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260503_104800_bc43ae09-f494-43e3-97d7-2f8c1692cfd7.mp4"
                  type="video/mp4"
                />
              </video>

              {/* Logo */}
              <div className="flex flex-row items-center relative z-10">
                <span className="font-['Space_Grotesk'] text-[24px] sm:text-[26px] font-bold text-white tracking-tight leading-none select-none">
                  Cynaps
                </span>
              </div>

              {/* Tagline */}
              <div className="mt-auto relative z-10">
                <p className="font-['Space_Grotesk'] text-[19px] font-medium text-white leading-[1.4]">
                  Validation infrastructure,
                  <br />
                  <span className="text-white/70 font-normal">that proves frontier AI.</span>
                </p>
              </div>
            </div>

            {/* Right Card — Dark Obsidian Card matching Cynaps Design System */}
            <div
              className="bg-[#0c0d12] border border-white/10 p-7 sm:p-10 flex flex-col justify-between relative shadow-[0_20px_50px_rgba(0,0,0,0.6)] rounded-[28px]"
            >
              {/* Columns */}
              <div className="relative pt-2 w-full h-full flex flex-col justify-between">
                <div className="flex flex-col md:flex-row justify-between gap-12 lg:gap-16 h-full">
                  
                  {/* Left: Nav & Company Links */}
                  <div className="flex flex-row gap-12 sm:gap-16 lg:gap-20">
                    {/* Column 1 — Navigation */}
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold mb-4 sm:mb-5 flex items-center gap-1.5">
                        <span className="text-zinc-600">//</span> Navigation
                      </h3>
                      <ul className="space-y-3 sm:space-y-3.5">
                        {navigationLinks.map((link) => (
                          <li key={link.name}>
                            <Link
                              to={link.href}
                              className="block font-sans text-sm text-zinc-400 hover:text-white transition-colors duration-150"
                            >
                              {link.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Column 2 — Company */}
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-400 font-semibold mb-4 sm:mb-5 flex items-center gap-1.5">
                        <span className="text-zinc-600">//</span> Company
                      </h3>
                      <ul className="space-y-3 sm:space-y-3.5">
                        {companyLinks.map((link) => (
                          <li key={link.name}>
                            <Link
                              to={link.href}
                              className="block font-sans text-sm text-zinc-400 hover:text-white transition-colors duration-150"
                            >
                              {link.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Right: Newsletter & Copyright */}
                  <div className="flex flex-col gap-8 md:max-w-[320px]">
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col">
                        <span className="font-['Space_Grotesk'] text-sm sm:text-base font-bold text-white tracking-tight">
                          Frontier evaluation dispatches.
                        </span>
                        <span className="font-mono text-xs text-zinc-400 mt-0.5">
                          Quarterly benchmark protocols & release notes.
                        </span>
                      </div>

                      <form
                        onSubmit={handleSubscribe}
                        className="flex flex-row w-full bg-black/60 border border-white/15 rounded-xl p-1 focus-within:border-white/40 focus-within:ring-1 focus-within:ring-white/20 transition-all"
                      >
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={isSubscribed ? "Thank you for subscribing!" : "Enter work email"}
                          className="flex-1 min-w-0 px-3.5 py-2 bg-transparent border-none outline-none font-sans text-xs sm:text-sm text-white placeholder-zinc-500"
                        />
                        <button
                          type="submit"
                          className="px-4 py-2 bg-white hover:bg-zinc-200 text-black font-semibold font-sans text-xs sm:text-sm rounded-lg transition-all duration-150 cursor-pointer whitespace-nowrap"
                        >
                          {isSubscribed ? "Subscribed" : "Subscribe"}
                        </button>
                      </form>
                    </div>

                    <div className="flex flex-col gap-1">
                      <div className="font-mono text-xs text-zinc-400">
                        © 2026 Cynaps Inc. All rights reserved.
                      </div>
                      <div className="font-mono text-[11px] text-zinc-500">
                        Independent validation infrastructure for frontier AI.
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Large Fluid Cynaps Watermark — 70% visible, smoothly fading down */}
          <div
            className="max-w-[1150px] -mt-10 sm:-mt-14 mx-auto pointer-events-none select-none relative z-0 leading-none overflow-hidden"
            aria-hidden="true"
            style={{
              maskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 30%, rgba(0,0,0,0.45) 65%, rgba(0,0,0,0.06) 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 30%, rgba(0,0,0,0.45) 65%, rgba(0,0,0,0.06) 100%)",
            }}
          >
            <svg
              ref={watermarkSvgRef}
              id="watermarkSvg"
              viewBox="62 24 876 195"
              preserveAspectRatio="xMidYMid meet"
              className="block w-full h-auto overflow-hidden"
            >
              <defs>
                <linearGradient id="cynapsWatermarkFade" x1="0" y1="0" x2="0" y2="0.7">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
                  <stop offset="25%" stopColor="#ffffff" stopOpacity="0.45" />
                  <stop offset="55%" stopColor="#ffffff" stopOpacity="0.25" />
                  <stop offset="85%" stopColor="#ffffff" stopOpacity="0.10" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <text
                ref={watermarkTextRef}
                id="watermarkText"
                x="500"
                y="240"
                textAnchor="middle"
                fontSize="320"
                fill="url(#cynapsWatermarkFade)"
                className="font-['Space_Grotesk',sans-serif] font-bold tracking-[-0.03em]"
              >
                Cynaps
              </text>
            </svg>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Footer;
